<script setup lang="ts">
import type { DateRange } from "reka-ui";
import type { Ref } from "vue";

import {
    CalendarDate,
    DateFormatter,
    getLocalTimeZone,
    today,
} from "@internationalized/date";
import { CalendarIcon } from "lucide-vue-next";
import { ref, computed, watch } from "vue";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { RangeCalendar } from "@/components/ui/range-calendar";

interface Props {
    modelValue?: DateRange;
    displayFullBlock?: DateRange;
}

interface Emits {
    (e: "update:modelValue", value: DateRange): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const df = new DateFormatter("en-US", {
    dateStyle: "medium",
});

const todayDate = today(getLocalTimeZone());

const internalValue = ref({
    start: props.modelValue?.start || todayDate.subtract({ days: 30 }),
    end: props.modelValue?.end || todayDate,
}) as Ref<DateRange>;

// Sync internal value with prop
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            internalValue.value = newValue;
        }
    },
    { deep: true },
);

// Emit changes to parent with debouncing to prevent double emissions
let emitTimeout: number | null = null;

watch(
    internalValue,
    (newValue) => {
        // Clear any pending emission
        if (emitTimeout) {
            clearTimeout(emitTimeout);
        }

        // Debounce the emission by 10ms to handle rapid start/end changes
        emitTimeout = setTimeout(() => {
            emit("update:modelValue", newValue);
            emitTimeout = null;
        }, 10);
    },
    { deep: true },
);

const value = computed({
    get: () => internalValue.value,
    set: (newValue) => {
        internalValue.value = newValue;
    },
});

interface QuickRange {
    label: string;
    start: CalendarDate;
    end: CalendarDate;
}

const quickRanges = computed<QuickRange[]>(() => [
    {
        label: "Today",
        start: todayDate,
        end: todayDate,
    },
    {
        label: "Yesterday",
        start: todayDate.subtract({ days: 1 }),
        end: todayDate.subtract({ days: 1 }),
    },
    {
        label: "Last 7 days",
        start: todayDate.subtract({ days: 7 }),
        end: todayDate,
    },
    {
        label: "Last 30 days",
        start: todayDate.subtract({ days: 30 }),
        end: todayDate,
    },
    {
        label: "Last 90 days",
        start: todayDate.subtract({ days: 90 }),
        end: todayDate,
    },
    {
        label: "This year",
        start: todayDate.set({ month: 1, day: 1 }),
        end: todayDate,
    },
    {
        label: "Last year",
        start: todayDate.subtract({ years: 1 }).set({ month: 1, day: 1 }),
        end: todayDate.subtract({ years: 1 }).set({ month: 12, day: 31 }),
    },
]);

const selectedRangeLabel = computed(() => {
    const currentRange = value.value;
    const matchingQuickRange = quickRanges.value.find(
        (range) =>
            range.start.compare(currentRange.start) === 0 &&
            range.end.compare(currentRange.end) === 0,
    );

    return matchingQuickRange?.label || "Custom range";
});

const selectQuickRange = (range: QuickRange) => {
    value.value = {
        start: range.start,
        end: range.end,
    };
};
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <Button
                variant="outline"
                :class="
                    cn(
                        props.displayFullBlock ? 'w-[280px]' : 'w-[40px]',
                        'h-12 justify-start text-left font-normal',
                        !value && 'text-muted-foreground',
                    )
                "
            >
                <CalendarIcon class="mr-2 h-7 w-7 shrink-0" />

                <div
                    v-if="props.displayFullBlock"
                    class="flex py-2 flex-col items-start"
                >
                    <span class="font-medium">{{ selectedRangeLabel }}</span>
                    <span
                        v-if="value.start && value.end"
                        class="text-xs text-gray-500"
                    >
                        {{ df.format(value.start.toDate(getLocalTimeZone())) }}
                        -
                        {{ df.format(value.end.toDate(getLocalTimeZone())) }}
                    </span>
                </div>
            </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0" align="end">
            <div class="flex">
                <!-- Quick Actions List -->
                <div class="w-40 border-r mt-4 border-gray-200 p-3">
                    <div class="space-y-1">
                        <button
                            v-for="range in quickRanges"
                            :key="range.label"
                            :class="
                                cn(
                                    'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                                    'hover:bg-gray-100',
                                    selectedRangeLabel === range.label
                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                        : 'text-gray-700',
                                )
                            "
                            @click="selectQuickRange(range)"
                        >
                            {{ range.label }}
                        </button>
                    </div>
                </div>

                <!-- Calendar -->
                <div class="p-3">
                    <RangeCalendar
                        v-model="value"
                        initial-focus
                        :number-of-months="1"
                        @update:start-value="
                            (startDate) => (value.start = startDate)
                        "
                    />
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
